import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { formatDate } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ReactiveFormsModule, FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, of, Observable, Subject } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ToastService } from '../../../ui';
import { ContractsService, ExtensionCatalogEntry } from '../contracts.service';
import { AuthService } from '../../../core/auth/auth.service';
import { SessionService } from '../../../core/session/session.service';
import { extensionDualName } from '../extension-name.util';
import { addableExtensions } from '../extension-availability.util';
import { todayIso } from '../../../shared/utils/iso-date.util';
import {
  HLM_DIALOG_SHELL,
  HLM_FORM_FIELD,
  HlmBtnDirective,
  HlmIconComponent,
  HlmComboboxComponent,
  HlmInputDirective,
  HlmCheckboxComponent,
  HlmSpinnerComponent,
  HlmAlertComponent,
} from '../../../ui';

export interface AddExtensionDialogData {
  contractId: number;
  customerId: number;
  invoiceDay: number;
  tariff: string;
  currentPlan: string;
  priceListName: string;
  currency: string;
}

/** Everything one reload of the offered set needs; `null` = contract not active on that date. */
type AddableLoad = {
  catalog: ExtensionCatalogEntry[];
  booked: { extensionPriceInfo: { name: string } }[];
  names: Record<string, string>;
  tariff: string;
  priceListName: string;
} | null;

/**
 * Old CMT's `roundTo` (`addingExtension.js:190-198`) scrubs the float error with `toFixed(11)`
 * *before* rounding. Plain `Math.round(v * 100) / 100` disagrees with it on thousands of
 * inputs — e.g. fee 8.00 / target 2.47 gives 69.12499999999999, which old CMT sends as 69.13
 * and a naive round sends as 69.12. Replicated verbatim (EB-4209 review run 2).
 */
function roundTo(value: number, digits: number): number {
  const factor = Math.pow(10, digits);
  const scrubbed = parseFloat((value * factor).toFixed(11));
  return Math.round(scrubbed) / factor;
}

@Component({
  selector: 'cmt-add-extension-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ...HLM_DIALOG_SHELL, ...HLM_FORM_FIELD,
    HlmBtnDirective, HlmIconComponent, HlmComboboxComponent,
    HlmInputDirective, HlmCheckboxComponent, HlmSpinnerComponent, HlmAlertComponent,
  ],
  template: `
    <hlm-dialog-surface size="md">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="primary">
          <hlm-icon name="lucidePlus" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading class="pr-8">
          <h2 hlmDialogTitle>Add Extension</h2>
          <p hlmDialogDescription>
            Contract
            <span class="font-mono-feat">{{ data.contractId }}</span> ·
            <strong>{{ data.currentPlan }}</strong> · {{ tariffLabel() }} · {{ priceListLabel() }}
          </p>
        </div>
        <button hlmDialogClose (click)="dialogRef.close()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody>
        <!-- The form stays mounted for every state: a date change reloads underneath it and a
             load failure is an alert above it with a Retry — replacing the body would remove the
             date field the operator needs in order to recover (EB-4210 review run 2). -->
        <form [formGroup]="form" class="flex flex-col gap-4">
          @if (errorMsg()) {
            <hlm-alert tone="error">
              <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5 text-destructive" />
              <span class="flex-1">{{ errorMsg() }}</span>
              <button hlmBtn variant="outline" size="sm" type="button" (click)="retry()">
                Retry
              </button>
            </hlm-alert>
          }

          @if (loading()) {
            <div class="flex items-center gap-3 py-1 text-sm text-muted-foreground" role="status">
              <hlm-spinner size="18" />
              <span>Loading extensions...</span>
            </div>
          }

          <hlm-form-field>
            <label hlmLabel for="extension">Extension</label>
            <hlm-combobox
              inputId="extension"
              [options]="extensionOptions()"
              placeholder="Select extension…"
              formControlName="extensionName"
            />
          </hlm-form-field>

          @if (dateNotice()) {
            <hlm-alert tone="error">
              <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5 text-destructive" />
              <span>{{ dateNotice() }}</span>
            </hlm-alert>
          }

          <hlm-form-field>
            <label hlmLabel for="startDate">Start date</label>
            <input id="startDate" hlmInput type="date" formControlName="startDate"
                   [min]="minDateStr" />
            @if (form.controls['startDate'].errors?.['minDate']) {
              <p hlmFieldError>Cannot be before {{ minDateStr }}.</p>
            }
          </hlm-form-field>

          @if (selectedExtension()?.multipleBookable) {
            <hlm-form-field>
              <label hlmLabel for="amount">Amount</label>
              <input id="amount" hlmInput type="number" formControlName="amount" min="1" step="1" />
            </hlm-form-field>
          }

          @if (selectedExtension()?.customPrice) {
            <hlm-form-field>
              <label hlmLabel for="targetPrice">Target price</label>
              <input id="targetPrice" hlmInput type="number" formControlName="targetPrice"
                     min="0" step="0.01" />
              <p hlmFieldHint>
                List price {{ selectedExtension()?.extensionPriceInfo.monthlyFee }}
                {{ data.currency }} · discount {{ discountPercent() }} %
              </p>
              @if (form.controls['targetPrice'].errors?.['required']) {
                <p hlmFieldError>Enter a target price.</p>
              }
            </hlm-form-field>
          }

          @if (selectedExtension()?.bookableToAllContracts) {
            <div class="flex flex-col gap-1">
              <hlm-checkbox formControlName="bookToAllContracts">
                Book to all contracts
              </hlm-checkbox>
              <p class="text-sm text-muted-foreground">
                Checked, the extension is booked on every contract of this customer.
              </p>
            </div>
          }
        </form>
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" (click)="dialogRef.close()" [disabled]="submitting()">
          Cancel
        </button>
        <button hlmBtn (click)="submit()" [disabled]="!canSubmit()">
          @if (submitting()) {
            <hlm-spinner size="16" /> Adding...
          } @else {
            <hlm-icon name="lucidePlus" size="0.9rem" />
            Add
          }
        </button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class AddExtensionDialogComponent implements OnInit {
  readonly data = inject<AddExtensionDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<AddExtensionDialogComponent>);
  private readonly contractsService = inject(ContractsService);
  private readonly authService = inject(AuthService);
  private readonly sessionService = inject(SessionService);
  private readonly toast = inject(ToastService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly errorMsg = signal<string | null>(null);
  /** Recoverable date problem — shown next to the still-editable date field. */
  readonly dateNotice = signal<string | null>(null);
  readonly submitting = signal(false);
  readonly extensions = signal<ExtensionCatalogEntry[]>([]);
  readonly extensionNames = signal<Record<string, string>>({});
  readonly selectedExtension = signal<ExtensionCatalogEntry | null>(null);
  readonly minDateStr = todayIso();
  /** Header follows the revision the offered list came from, not the one open at dialog start. */
  readonly tariffLabel = signal(this.data.tariff);
  readonly priceListLabel = signal(this.data.priceListName);

  /** Dates to load the offered set for; only the newest one wins (see `ngOnInit`). */
  private readonly dateChange = new Subject<string>();

  private readonly startDateNotBeforeToday: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string | null;
    if (!value) return null;
    // Angular's MinValidator only binds to input[type=number], so the template's [min] is
    // decoration — without this a hand-typed past date would be submittable (review run 2).
    return value < this.minDateStr ? { minDate: { min: this.minDateStr } } : null;
  };

  readonly form: FormGroup = this.fb.group({
    extensionName: [null as string | null, Validators.required],
    startDate: [this.minDateStr, [Validators.required, this.startDateNotBeforeToday]],
    amount: [1, [Validators.required, Validators.min(1)]],
    // `required` matters: min(0) passes for an empty field, and `1 - null/fee` would send a
    // 100 % discount, i.e. book the extension for free (review run 2).
    targetPrice: [0, [Validators.required, Validators.min(0)]],
    // Old CMT books a bookable-to-all extension to every contract of the customer unless the
    // operator unchecks this (`addingExtension.js:11`).
    bookToAllContracts: [true],
  });

  /**
   * Mirror of the `targetPrice` control. `discountPercent` is a `computed`, and reading
   * `this.form.value` inside it registers no dependency — the hint would stay cached until the
   * *selection* changed, showing 0 % while the request carried the real discount (PR #40
   * review). Both writers feed this signal: the control's `valueChanges` (operator typing) and
   * `applySelection`, which seeds the control with `emitEvent: false` on purpose.
   */
  private readonly targetPriceValue = signal<number | null>(0);

  /** The discount the current target price implies — shown as a hint so it is never a surprise. */
  readonly discountPercent = computed(() => {
    const ext = this.selectedExtension();
    const fee = ext?.extensionPriceInfo.monthlyFee ?? 0;
    const target = this.targetPriceValue();
    if (!ext?.customPrice || fee <= 0 || target == null) return 0;
    return roundTo((1 - target / fee) * 100, 2);
  });

  ngOnInit(): void {
    // switchMap on the date stream, so a slow response for an abandoned date can never
    // overwrite the list of the date the operator is looking at. The requested date travels
    // with the result — reading the control in the response handler would format whatever is
    // in the field by then, and `formatDate('')` throws (EB-4210 review run 2).
    this.dateChange
      .pipe(
        tap(() => {
          this.loading.set(true);
          this.errorMsg.set(null);
          this.dateNotice.set(null);
        }),
        switchMap((dateIso) =>
          this.loadAddable(dateIso).pipe(
            map((result) => ({ result, dateIso })),
            catchError(() => of({ result: 'error' as const, dateIso })),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ result, dateIso }) => this.applyLoad(result, dateIso));

    // Catalog and booked set both depend on the start date, so re-derive the options
    // whenever it changes (old CMT: `$watch('extensionToAddFromDate')`).
    this.form.get('startDate')!.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((startDate: string | null) => {
        if (startDate) this.dateChange.next(startDate);
      });

    this.form.get('extensionName')!.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((name: string | null) => this.applySelection(this.findExtension(name)));

    this.form.get('targetPrice')!.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: number | null) => this.targetPriceValue.set(value));

    this.dateChange.next(this.minDateStr);
  }

  /** Re-run the load for the date currently in the form (or today) after a failure. */
  retry(): void {
    const startDate = (this.form.value.startDate as string | null) ?? this.minDateStr;
    this.errorMsg.set(null);
    this.dateChange.next(startDate);
  }

  /**
   * Addable = the extensions bookable on the revision's **plan** minus the ones already booked
   * on that date; booking the same extension twice is not allowed (EB-4210). The plan-scoped
   * list (`system=true`) is old CMT's source here (`addingExtension.js:119`) — the price-list
   * catalog is a different, narrower set that old CMT only uses as the Remove gate (EB-4209
   * review run 2). Emits `null` when the contract is not active, or has no plan, on that date.
   */
  private loadAddable(dateIso: string): Observable<AddableLoad> {
    if (!dateIso) return of(null);
    const brandId = this.sessionService.brandId() ?? 1;
    // Extension names must be resolved in the customer's language, not a hardcoded
    // one — old CMT passes `customer.mainCustomerLanguage` (EB-4143).
    const languageId = this.sessionService.customerLanguage() ?? 1;
    return this.contractsService.getRevisionValidOn(this.data.contractId, dateIso).pipe(
      switchMap((revision) => {
        if (!revision?.planId) return of(null);
        return forkJoin({
          catalog: this.contractsService.getExtensionsByPlan(revision.planId),
          booked: this.contractsService.getBookedExtensions(this.data.contractId, dateIso),
          names: this.contractsService.getExtensionNames(languageId, brandId).pipe(catchError(() => of({}))),
        }).pipe(
          map((loaded) => ({
            ...loaded,
            names: loaded.names as Record<string, string>,
            tariff: revision.tariff,
            priceListName: revision.priceListName,
          })),
        );
      }),
    );
  }

  private applyLoad(result: AddableLoad | 'error', dateIso: string): void {
    if (result === 'error') {
      this.errorMsg.set('Failed to load extension catalog');
      this.loading.set(false);
      return;
    }
    // Read the selection now, not when the request went out — the operator may have picked
    // an extension while it was in flight (EB-4210 review).
    const selectedName = this.form.value.extensionName as string | null;

    if (!result) {
      this.extensions.set([]);
      this.restoreSelection(selectedName);
      // Wording copied from old CMT `addingExtension.js:152`. `dateIso` is the date the request
      // was made for, so this cannot format an empty control value.
      this.dateNotice.set(
        `Contract is not active on the date: ${formatDate(dateIso, 'dd.MM.yyyy', 'en')}. Please select another day.`,
      );
      this.loading.set(false);
      return;
    }
    const { catalog, booked, names, tariff, priceListName } = result;
    // Keep the labels we already have if the localization call came back empty, otherwise a
    // flaky request would regress every option to its technical name and reorder the list.
    if (Object.keys(names).length > 0) this.extensionNames.set(names);
    this.tariffLabel.set(tariff);
    this.priceListLabel.set(priceListName);
    // Sort by display name; the options carry the technical name as their value, so the order
    // is presentation only and can never desynchronise from what `submit()` resolves.
    const addable = addableExtensions(catalog, booked).sort((a, b) =>
      this.extLabel(a).localeCompare(this.extLabel(b)),
    );
    this.extensions.set(addable);
    this.restoreSelection(selectedName);
    if (addable.length === 0) {
      this.dateNotice.set('All available extensions are already booked on this date.');
    }
    this.loading.set(false);
  }

  private findExtension(name: string | null): ExtensionCatalogEntry | null {
    if (!name) return null;
    return this.extensions().find((e) => e.extensionPriceInfo.name === name) ?? null;
  }

  /** Keep the selection across a reload when the same extension is still offered. */
  private restoreSelection(name: string | null): void {
    const ext = this.findExtension(name);
    this.form.get('extensionName')!.setValue(ext ? name : null, { emitEvent: false });
    this.applySelection(ext);
  }

  /**
   * Single writer for everything derived from the selected extension. Old CMT seeds the target
   * price with the list price so the default discount is 0 (`addingExtension.js:78-83`); leaving
   * it at 0 would book the extension with a 100 % discount.
   *
   * The seed is skipped while the operator has edited the field (`dirty`) so a reload cannot
   * discard a typed price — but it does follow a *changed list price*, which happens when the
   * same extension costs something else in the revision the operator moved to.
   */
  private applySelection(ext: ExtensionCatalogEntry | null): void {
    const previous = this.selectedExtension();
    this.selectedExtension.set(ext);

    const targetPrice = this.form.get('targetPrice')!;
    const fee = ext?.extensionPriceInfo.monthlyFee ?? 0;
    const nameChanged = (ext?.extensionPriceInfo.name ?? null) !== (previous?.extensionPriceInfo.name ?? null);
    const feeChanged = (previous?.extensionPriceInfo.monthlyFee ?? null) !== (ext?.extensionPriceInfo.monthlyFee ?? null);
    if (nameChanged || (feeChanged && targetPrice.pristine)) {
      targetPrice.setValue(fee, { emitEvent: false });
      // `emitEvent: false` skips `valueChanges`, so the hint's mirror is written here.
      this.targetPriceValue.set(fee);
      if (nameChanged) targetPrice.markAsPristine();
    }

    // `amount` is required + min(1) but its input only renders for multipleBookable extensions.
    // Without this reset a cleared Amount would keep the form invalid — with no visible field to
    // fix — after switching to an extension that books single (review run 2).
    if (nameChanged) this.form.get('amount')!.setValue(1, { emitEvent: false });
  }

  /** "Readable (technical)" extension name (EB-4006). */
  extLabel(ext: ExtensionCatalogEntry): string {
    return extensionDualName(this.extensionNames(), ext.extensionPriceInfo.labelKey, ext.extensionPriceInfo.name);
  }

  /** Options for the searchable select, keyed by technical name: "Readable (technical) — price". */
  readonly extensionOptions = computed(() =>
    this.extensions().map((ext) => ({
      value: ext.extensionPriceInfo.name,
      label: `${this.extLabel(ext)} — ${ext.extensionPriceInfo.monthlyFee} ${this.data.currency}`,
    })),
  );

  canSubmit(): boolean {
    return this.form.valid && !this.loading() && !this.submitting() && !this.errorMsg();
  }

  submit(): void {
    if (!this.canSubmit()) return;
    const ext = this.findExtension(this.form.value.extensionName as string | null);
    const targetPrice = this.form.value.targetPrice as number | null;
    if (!ext || targetPrice == null) return;
    this.submitting.set(true);

    const fee = ext.extensionPriceInfo.monthlyFee;
    // bookingmgmt takes the discount as a PERCENT with 2 decimals — old CMT computes
    // `(1 - target/fee) * 100` here (`addingExtension.js:25-32`), while the contract API's
    // discount PATCH takes a fraction (`contract.revisions.service.js:81`). A target price
    // above the list price yields a negative discount (a surcharge), as in old CMT.
    const discount = ext.customPrice && fee > 0 ? roundTo((1 - targetPrice / fee) * 100, 2) : 0;

    // Synchronous current-user read — the canonical pattern hardened in EB-3922
    // (replaces the old user$.subscribe().unsubscribe() anti-pattern; matches change-discount-dialog).
    this.contractsService.addExtension(this.data.contractId, {
      name: ext.extensionPriceInfo.name,
      count: ext.multipleBookable ? Math.trunc(this.form.value.amount as number) : 1,
      discount,
      startDate: this.form.value.startDate as string,
      bookToAllContracts: ext.bookableToAllContracts ? Boolean(this.form.value.bookToAllContracts) : false,
      employeeId: this.authService.currentUserId,
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toast.open('Extension added successfully', 'OK', { duration: 4000 });
        this.dialogRef.close(true);
      },
      error: () => {
        this.toast.open('Failed to add extension', 'OK', { duration: 6000 });
        this.submitting.set(false);
      },
    });
  }
}
